import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import { collection } from 'utils/immutable';
import * as editableDocument from 'utils/model/editableDocument';
import OfferPaymentPlanAdvanceSplitComponent from './OfferPaymentPlanAdvanceSplitComponent';
import { compose } from 'redux';
import { saveAdvanceSplit } from '../actionCreators';
import { useDispatch } from 'react-redux';
import EditAdvance from '@mui/icons-material/Edit';
import { CollapseCard } from '@totalsoft/rocket-ui';

//eslint-disable-next-line react/display-name
export const getLabelValue = t => (value, valueType, format) => {
  switch (valueType) {
    case 'System.DateTime':
      return t('DATE', { date: value });
    case 'System.Decimal': {
      const scale = format && format.includes('n4') ? 4 : 2;
      return <NumberFormat thousandSeparator={true} decimalScale={scale} displayType='text' value={value} />;
    }
    default:
      return value;
  }
};

const OfferPaymentPlanAdvanceSplitCard = ({ advanceSplit, advanceData, t, saving }) => {
  const getLocalAdvanceSplit = () => {
    return {
      ...advanceSplit,
      isDirty: false,
      splitValues: advanceSplit.splitValues.map(editableDocument.create)
    };
  };
  const [localAdvanceSplit, setLocalAdvanceSplit] = useState(getLocalAdvanceSplit());

  const dispatch = useDispatch();

  useEffect(() => {
    setLocalAdvanceSplit(getLocalAdvanceSplit());
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanceSplit]);

  const addNewItem = () => {
    const newRow = editableDocument.create({ dueDate: '', advanceAmount: '', orderNo: '', isEditable: true });

    setLocalAdvanceSplit({
      ...localAdvanceSplit,
      isDirty: true,
      splitValues: collection.insert(localAdvanceSplit.splitValues.length, newRow, localAdvanceSplit.splitValues)
    });
  };

  const deleteItem = index => {
    setLocalAdvanceSplit({
      ...localAdvanceSplit,
      isDirty: true,
      splitValues: collection.remove(index, localAdvanceSplit.splitValues)
    });
  };

  const changeItemProperty = (index, prop, value) => {
    const newColl = collection.update(index, editableDocument.update(prop, value, null), localAdvanceSplit.splitValues);

    setLocalAdvanceSplit({
      ...localAdvanceSplit,
      splitValues: newColl,
      isDirty: true
    });

    checkSumOfAdvanceSplitValuesValid();
  };

  const changeAdvanceIsSplitted = () => {
    setLocalAdvanceSplit({
      ...localAdvanceSplit,
      advanceSplitIsAmount: localAdvanceSplit.advanceSplitIsAmount ? false : true,
      isDirty: true
    });
  };

  const checkSumOfAdvanceSplitValuesValid = () => {
    if (localAdvanceSplit.splitValues.length > 0) {
      const sumOfAdvanceSplitValues = localAdvanceSplit.splitValues.reduce((a, b) => a + b.document.advanceAmount, 0);
      if (localAdvanceSplit.advanceSplitIsAmount) return sumOfAdvanceSplitValues === advanceData.advance;
      else return sumOfAdvanceSplitValues === 100;
    }
    return true;
  };

  const save = async () => {
    const savedata = {
      documentId: localAdvanceSplit.documentId,
      siteId: localAdvanceSplit.siteId,
      advanceSplitIsAmount: localAdvanceSplit.advanceSplitIsAmount,
      splitValues: localAdvanceSplit.splitValues.map(a => a.document)
    };

    await dispatch(saveAdvanceSplit(savedata));

    setLocalAdvanceSplit({
      ...localAdvanceSplit,
      isDirty: false
    });
  };

  return (
    <CollapseCard id={'offerPaymentPlanAdvanceSplitCard'} icon={EditAdvance} title={t('OfferEdit.AdvanceSplit.Title')} toggleOnHeaderClick>
      <OfferPaymentPlanAdvanceSplitComponent
        advanceSplit={localAdvanceSplit}
        splitValues={localAdvanceSplit.splitValues}
        onAdd={addNewItem}
        onPropertyChange={changeItemProperty}
        onSave={save}
        changeAdvanceIsSplitted={changeAdvanceIsSplitted}
        onDelete={deleteItem}
        saving={saving}
        checkSumOfAdvanceSplitValuesValid={checkSumOfAdvanceSplitValuesValid}
        advanceData={advanceData}
      />
    </CollapseCard>
  );
};

OfferPaymentPlanAdvanceSplitCard.displayName = 'OfferPaymentPlanAdvanceSplitCard';

OfferPaymentPlanAdvanceSplitCard.propTypes = {
  t: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  advanceSplit: PropTypes.object.isRequired,
  advanceData: PropTypes.object
};

const ConnectedComponent = compose(withTranslation())(OfferPaymentPlanAdvanceSplitCard);
export default ConnectedComponent;
